import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'
import { logger } from '@/utils/logger'

import { ApiTaCm010 } from '~/endpoints/ApiTaCm010'
import { ApiTaCm030 } from '~/endpoints/ApiTaCm030'
import { ApiTaRe040 } from '~/endpoints/ApiTaRe040'
import { ApiTaRe050 } from '~/endpoints/ApiTaRe050'
import { ApiTaRe180 } from '~/endpoints/ApiTaRe180'

// State types
interface ContractInfo {
  currentCourseId: string
  contractInfo: {
    courseId: string
    planId: string
    levelId: string
    yyMm: {
      materialYyMm: number
      subjectId: string
      isDeliveried: number
      logicalYear: number
    }[]
  }[]
  isContractInfo: number
}
const getEmptyContractInfo = (): ContractInfo => ({
  currentCourseId: '',
  contractInfo: [],
  isContractInfo: 0,
})

interface MonthList {
  reportType: number
  reportList: {
    reportId: string
    memNum: string
    grade: number
    fiscalYear: number
    attendedMonth: number
    testCountPerMonth: number
    isRead?: boolean
    testType: number
  }[]
}
const getEmptyMonthList = (): MonthList => ({
  reportType: 0,
  reportList: [],
})

interface MonthlyTestResult {
  monthlyTestResults: {
    memNum: string
    title: string
    gradeDataResults: {
      subjectCode: string
      answerId: string
      score: string
      average: string
      deviationValue: string
      isProcessed: boolean
    }[]
    difficultyResults: {
      subjectCode: string
      resultsByQuestion:
        | {
            pieceId: string
            unitId: string
            contentId: string
            questionNo: number
            basicsScoreRate: string
            practicalScoreRate: string
          }[]
        | null
    }[]
  }
}
const getEmptyMonthlyTestResult = (): MonthlyTestResult => ({
  monthlyTestResults: {
    memNum: '',
    title: '',
    gradeDataResults: [],
    difficultyResults: [],
  },
})

interface MonthlyTestSubjectResult {
  monthlyTestSubjectGrade: {
    memNum: string
    results: {
      questionNo: string
      subQuestionNo: string
      difficultyLevel: string
      allocation: string
      score: string
      average: string
    }[]
  }
}
const getEmptyMonthlyTestSubjectResult = (): MonthlyTestSubjectResult => ({
  monthlyTestSubjectGrade: {
    memNum: '',
    results: [],
  },
})

interface MonthlyTestRank {
  results: {
    ranking: string
    score: string
    nickName: string
    schoolName: string
  }[]
}
const getEmptyMonthlyTestRank = (): MonthlyTestRank => ({
  results: [],
})

// State action parameter
interface ActionParamsMonthList {
  reportType: number
  offset: string
  limit: number
}

interface ActionParamsMonthlyTestResult {
  fiscalYear: number
  month: number
  times: number
  grade: number
  courseId: string
}

interface ActionParamsMonthlyTestSubjectResult {
  fiscalYear: number
  month: number
  times?: number
  grade: number
  courseId: string
  subjectCode: string
}

interface ActionParamsMonthlyTestRank {
  fiscalYear: number
  testMonth: number
  testCountPerMonth?: number
  subjectCode: string
  grade: number
  courseId: string
}

@Module({
  stateFactory: true,
  namespaced: true,
  name: 'apis/MonthlyTest',
})
export default class MonthlyTest extends VuexModule {
  // States
  private contractInfo_ = getEmptyContractInfo()

  private monthList_ = getEmptyMonthList()

  private hasMoreMonthList_ = true

  private monthlyTestResult_ = getEmptyMonthlyTestResult()

  private monthlyTestSubjectResult_ = getEmptyMonthlyTestSubjectResult()

  private monthlyTestRank_ = getEmptyMonthlyTestRank()

  // Getters
  get contractInfo(): ContractInfo {
    return this.contractInfo_
  }

  get monthList(): MonthList {
    return this.monthList_
  }

  get hasMoreMonthList(): boolean {
    return this.hasMoreMonthList_
  }

  get monthlyTestResult(): MonthlyTestResult {
    return this.monthlyTestResult_
  }

  get monthlyTestSubjectResult(): MonthlyTestSubjectResult {
    return this.monthlyTestSubjectResult_
  }

  get monthlyTestRank(): MonthlyTestRank {
    return this.monthlyTestRank_
  }

  // Mutations
  @Mutation
  updateContractInfo(value: ContractInfo) {
    this.contractInfo_ = value
  }

  @Mutation
  updateMonthList(value: MonthList) {
    this.monthList_.reportType = value.reportType
    this.monthList_.reportList = this.monthList_.reportList.concat(value.reportList)
  }

  @Mutation
  updateMonthListReadFlg(index: number) {
    this.monthList_.reportList[index].isRead = true
  }

  @Mutation
  clearMonthList() {
    this.monthList_ = getEmptyMonthList()
  }

  @Mutation
  updateHasMoreMonthList(flg: boolean) {
    this.hasMoreMonthList_ = flg
  }

  @Mutation
  clearHasMoreMonthList() {
    this.hasMoreMonthList_ = true
  }

  @Mutation
  updateMonthlyTestResult(value: MonthlyTestResult) {
    this.monthlyTestResult_ = value
  }

  @Mutation
  updateMonthlyTestSubjectResult(value: MonthlyTestSubjectResult) {
    this.monthlyTestSubjectResult_ = value
  }

  @Mutation
  updateMonthlyTestRank(value: MonthlyTestRank) {
    this.monthlyTestRank_ = value
  }

  // Actions

  @Action
  clear() {
    this.context.commit('updateContractInfo', getEmptyContractInfo())
    this.context.commit('clearMonthList')
    this.context.commit('clearHasMoreMonthList')
    this.context.commit('updateMonthlyTestResult', getEmptyMonthlyTestResult())
    this.context.commit('updateMonthlyTestSubjectResult', getEmptyMonthlyTestSubjectResult())
    this.context.commit('updateMonthlyTestRank', getEmptyMonthlyTestRank())
  }

  @Action
  async refreshContractInfo() {
    logger.trace('in: refreshContractInfo')
    const api = new ApiTaCm010.Api()

    // API: ApiTaCm010
    const result = await api.get<ContractInfo | null>(
      { query: {} },
      // success
      (data, _) => {
        return {
          currentCourseId: data.currentCourseId,
          contractInfo: data.contractInfo,
          isContractInfo: data.isContractInfo,
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )
    if (result === null) {
      return
    }
    this.context.commit('updateContractInfo', result)
  }

  @Action({ rawError: true })
  async refreshMonthList(param: ActionParamsMonthList) {
    logger.trace('in: refreshMonthList')
    const api = new ApiTaCm030.Api()

    this.context.commit('updateHasMoreMonthList', false)

    // API: ApiTaCm030
    const result = await api.get<MonthList | null>(
      {
        query: {
          reportType: param.reportType,
          offset: param.offset,
          limit: param.limit,
        },
      },
      // success
      (data, _) => {
        return {
          reportType: data.reportType,
          reportList: data.reportList,
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )
    if (result === null || result === undefined) {
      this.context.commit('updateHasMoreMonthList', true)
      return
    }

    this.context.commit('updateHasMoreMonthList', result.reportList.length === param.limit)
    this.context.commit('updateMonthList', result)
  }

  @Action
  setMonthListReadFlg(index: number) {
    this.context.commit('updateMonthListReadFlg', index)
  }

  @Action
  async refreshMonthlyTestResult(param: ActionParamsMonthlyTestResult) {
    logger.trace('in: refreshMonthlyTestResult')
    const api = new ApiTaRe040.Api()

    // API: ApiTaRe040
    const result = await api.get<MonthlyTestResult | null>(
      {
        query: {
          fiscalYear: param.fiscalYear,
          month: param.month,
          times: param.times,
          grade: param.grade,
          courseId: param.courseId,
        },
      },
      // success
      (data, _) => {
        return {
          monthlyTestResults: {
            memNum: data.monthlyTestResults.memNum,
            title: data.monthlyTestResults.title,
            gradeDataResults: data.monthlyTestResults.gradeDataResults.map(elem => {
              return {
                subjectCode: elem.subjectCode,
                answerId: elem.answerId,
                score: elem.score,
                average: elem.average,
                deviationValue: elem.deviationValue,
                // 月次成績処理が実行されていない場合、ranking、average、deviationValueが「ー（ハイフン）」で返却される
                isProcessed: elem.average !== '-' || elem.deviationValue !== '-',
              }
            }),
            difficultyResults: data.monthlyTestResults.difficultyResults,
          },
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )
    if (result === null) {
      return
    }
    this.context.commit('updateMonthlyTestResult', result)
  }

  @Action
  async refreshMonthlyTestSubjectResult(param: ActionParamsMonthlyTestSubjectResult) {
    logger.trace('in: refreshMonthlyTestSubjectResult')
    const api = new ApiTaRe050.Api()
    const request: ApiTaRe050.Get.Request = {
      query: {
        fiscalYear: param.fiscalYear,
        month: param.month,
        grade: param.grade,
        courseId: param.courseId,
        subjectCode: param.subjectCode,
      },
    }
    if (param.times) {
      request.query.times = param.times
    }

    // API: ApiTaRe050
    const result = await api.get<MonthlyTestSubjectResult | null>(
      request,

      // success
      (data, _) => {
        return {
          monthlyTestSubjectGrade: data.monthlyTestSubjectGrade,
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )
    if (result === null) {
      return
    }
    this.context.commit('updateMonthlyTestSubjectResult', result)
  }

  @Action
  async refreshMonthlyTestRank(param: ActionParamsMonthlyTestRank) {
    logger.trace('in: refreshMonthlyTestRank')
    const api = new ApiTaRe180.Api()
    const request: ApiTaRe180.Get.Request = {
      query: {
        fiscalYear: param.fiscalYear,
        testMonth: param.testMonth,
        subjectCode: param.subjectCode,
        grade: param.grade,
        courseId: param.courseId,
      },
    }
    if (param.testCountPerMonth) {
      request.query.testCountPerMonth = param.testCountPerMonth
    }

    // API: ApiTaRe180
    const result = await api.get<MonthlyTestRank | null>(
      request,

      // success
      (data, _) => {
        return {
          results: data.results,
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )
    if (result === null) {
      return
    }
    this.context.commit('updateMonthlyTestRank', result)
  }
}
